import PropTypes from 'prop-types';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CONNECTION_STATE_CHANGE, generateClient } from 'aws-amplify/data';
import { Hub } from 'aws-amplify/utils';
import { onCreateSensorReadings } from '../graphql/subscriptions';
import { useGeneralContext } from './GeneralContext';

const SensorReadingsContext = createContext();

const client = generateClient();

export const defaultTimeout = 3 * 60 * 60 * 1000; // 3 hours in milliseconds

const Provider = ({ children }) => {
  const { user } = useGeneralContext();
  const [connection, setConnection] = useState({
    status: 'Disconnected',
    updated: new Date(),
  });
  const [readings, setReadings] = useState({});
  const connectionClient = useRef(null);

  const subscribe = useCallback(() => {
    try {
      if (!user) return;
      if (connectionClient.current) connectionClient.current.unsubscribe();
      connectionClient.current = client
        .graphql({
          query: onCreateSensorReadings,
          variables: { user_id: user.email },
        })
        .subscribe({
          next: ({ data: { onCreateSensorReadings } }) => {
            const localReadings = {};
            onCreateSensorReadings.reads.forEach(reading => {
              const itemCalc = {
                ...reading,
                parameters: JSON.parse(reading.parameters),
                timeToSave: Date.now() - reading.timestamp,
                receivedAt: Date.now(),
              };
              localReadings[reading.sensor_id] = itemCalc;
            });
            setReadings(prev => ({ ...prev, ...localReadings }));
          },
          error: error => {
            console.warn(error);
            setConnection({ status: 'Error', updated: new Date() });
          },
        });
    } catch (error) {
      console.error(error);
      setConnection({ status: 'Error', updated: new Date() });
    }
  }, [user]);

  const unsubscribe = () => {
    connectionClient.current?.unsubscribe();
    setConnection({ status: 'Disconnected', updated: new Date() });
  };

  useEffect(() => {
    const hubListenerCancelToken = Hub.listen('api', ({ payload }) => {
      if (payload.event === CONNECTION_STATE_CHANGE) {
        const { connectionState } = payload.data;
        setConnection({ status: connectionState, updated: new Date() });
      }
    });

    const timeout = Number(user?.['custom:timeout']) || defaultTimeout;
    let interval;
    if (timeout > 0) interval = setInterval(subscribe, timeout);
    subscribe();
    return () => {
      unsubscribe();
      hubListenerCancelToken();
      clearInterval(interval);
    };
  }, [subscribe, user]);

  const value = useMemo(
    () => ({ connection, readings, subscribe, unsubscribe }),
    [readings, connection, subscribe],
  );

  return (
    <SensorReadingsContext.Provider value={value}>
      {children}
    </SensorReadingsContext.Provider>
  );
};

export const useSensorReadingsContext = () => useContext(SensorReadingsContext);

Provider.propTypes = {
  children: PropTypes.element.isRequired,
};

export default Provider;
